import { ThemeProvider } from '@mui/material'
import { useEffect } from 'react'
import AppLayout from '../../components/AppLayout/AppLayout'
import { isGuest } from '../../helpers'
import { Dialogs, useDialogs } from '../../sharedComponents/src/Contexts/DialogsContext'
import { LoginState } from '../../sharedComponents/src/globalHeader/GlobalHeader'
import { useScreenSize } from '../../sharedComponents/src/hooks/useScreenSize'
import theme from '../../sharedComponents/src/Theme/theme-v2'
import { useStoreActions, useStoreState } from '../../store/hooks'
import PuzzlesChessBoard from '../chessboard/PuzzlesChessboard'
import PuzzlesLimitDialog from '../PuzzlesView/components/v2/PuzzleLimitDialog'
import useResultSounds from '../PuzzlesView/hooks/useResultSounds'
import { BoardOverlay } from './components/BoardOverlay'
import { StepChallengeProgress } from './components/StepChallengeProgress'
import { DesktopPQ } from './layouts/DesktopPQ'
import { MobilePQ } from './layouts/MobilePQ'
import { autoFeedTimeout } from './PuzzleQuestModel'

export const PuzzleQuest = () => {
    const settings = useStoreState((state) => state.gameView.settings)
    const { registerDialog, unregisterDialog } = useDialogs()
    const playResultSound = useResultSounds(settings)
    const { isMobile } = useScreenSize()

    const currentPosition = useStoreState((state) => state.puzzleQuestView.currentPosition)
    const stepProgress = useStoreState((state) => state.puzzleQuestView.stepProgress)
    const flipped = useStoreState((state) => state.puzzleQuestView.flipped)
    const gameTree = useStoreState((state) => state.puzzleQuestView.gameTree)
    const hint = useStoreState((state) => state.puzzleQuestView.hint)
    const customArrows = useStoreState((state) => state.puzzleQuestView.customArrows)
    const moveStatus = useStoreState((state) => state.puzzleQuestView.moveStatus)
    const myColor = useStoreState((state) => state.puzzleQuestView.myColor)
    const nextPuzzle = useStoreState((state) => state.puzzleQuestView.nextPuzzle)
    const puzzleState = useStoreState((state) => state.puzzleQuestView.puzzleState)
    const showSolution = useStoreState((state) => state.puzzleQuestView.showSolution)
    const showingSolution = useStoreState((state) => state.puzzleQuestView.showingSolution)
    const hintsAmount = useStoreState((state) => state.dailyBonus.hintsAmount)
    const userData = useStoreState((state) => state.userData)

    const currentPuzzleNumber = useStoreState((state) => state.puzzleQuestView.currentPuzzleNumber)

    const autoMove = useStoreActions((actions) => actions.puzzleQuestView.autoMove)
    const savePuzzle = useStoreActions((actions) => actions.puzzleQuestView.savePuzzle)
    const getPuzzle = useStoreActions((actions) => actions.puzzleQuestView.getPuzzle)
    const handleMove = useStoreActions((actions) => actions.puzzleQuestView.handleMove)
    const setAvailableHints = useStoreActions((actions) => actions.puzzleQuestView.setAvailableHints)
    const setShowHintLimit = useStoreActions((actions) => actions.puzzleQuestView.setShowHintLimit)

    // Register puzzle limit dialog
    useEffect(() => {
        if (stepProgress.isLimitReached) {
            const variant = isGuest(userData.userData?.groups) ? 'puzzleQuestMember' : 'puzzleQuestPremium'
            registerDialog(Dialogs.PUZZLE_LIMIT, <PuzzlesLimitDialog variant={variant} />)
        } else {
            unregisterDialog(Dialogs.PUZZLE_LIMIT)
        }
        return () => {
            unregisterDialog(Dialogs.PUZZLE_LIMIT)
        }
    }, [stepProgress.isLimitReached, userData.userData?.groups, registerDialog, unregisterDialog])

    // AUTO MOVE OR SHOW SOLUTION
    useEffect(() => {
        if ((puzzleState === null && !stepProgress.completed) || showingSolution) {
            if (currentPosition.position.turn !== myColor || showSolution) {
                autoMove()
            }
        }
    }, [currentPosition, myColor, gameTree, showSolution, puzzleState, stepProgress])

    // HANDLE SWITCH TO NEXT PUZZLE WITH 15 SECS DELAY
    useEffect(() => {
        let timeout

        if (nextPuzzle && !showSolution) {
            timeout = setTimeout(() => {
                savePuzzle(nextPuzzle)
            }, autoFeedTimeout) // 15s
        }
        return () => clearTimeout(timeout)
    }, [nextPuzzle, showSolution])

    // SOUND FOR MOVE
    useEffect(() => {
        if (puzzleState !== null) {
            puzzleState ? playResultSound(true) : playResultSound(false)
        }
    }, [puzzleState])

    useEffect(() => {
        // TIMEOUT TO PREVENT DOUBLE FETCH
        const timeout = setTimeout(() => {
            userData.state === LoginState.LOGGED_IN && getPuzzle()
        }, 1)
        return () => {
            clearTimeout(timeout)
            nextPuzzle && savePuzzle(nextPuzzle)
        }
    }, [userData.state, userData.userData?.user_name])

    // update available hints after claim bonus
    useEffect(() => {
        if (hintsAmount !== -1) {
            setAvailableHints(hintsAmount)
        }
    }, [hintsAmount])

    // HANDLE CLICK ANYWHERE
    const handleClickAnywhere = () => {
        setShowHintLimit(false)
    }

    useEffect(() => {
        document.addEventListener('mousedown', handleClickAnywhere)
        return () => {
            document.removeEventListener('mousedown', handleClickAnywhere)
        }
    }, [])

    if (!stepProgress.endTime && userData.state === LoginState.LOGGED_IN) return null

    const hideBoardOnMobile = stepProgress.completed && isMobile

    return (
        <AppLayout
            background="puzzleQuest"
            compactChild={!hideBoardOnMobile}
            boardHidden={hideBoardOnMobile}
            aboveBoard={
                isMobile && !stepProgress.completed ? (
                    <ThemeProvider theme={theme}>
                        <StepChallengeProgress
                            progress={stepProgress.progress}
                            currentPuzzle={currentPuzzleNumber}
                            endTime={stepProgress.endTime}
                        />
                    </ThemeProvider>
                ) : undefined
            }
            board={
                <>
                    <PuzzlesChessBoard
                        mode="puzzleQuest"
                        gameTree={gameTree}
                        currentPositionId={currentPosition.id}
                        flipped={flipped}
                        onMove={handleMove}
                        myColor={myColor}
                        hint={hint}
                        disableBoard={currentPosition.position.turn !== myColor || showSolution}
                        moveStatus={moveStatus}
                        customArrows={customArrows}
                    />
                    {!stepProgress.completed && userData.state === LoginState.LOGGED_IN && <BoardOverlay />}
                </>
            }
        >
            <ThemeProvider theme={theme}>{isMobile ? <MobilePQ /> : <DesktopPQ />}</ThemeProvider>
        </AppLayout>
    )
}
